import { AnimatePresence, m } from 'motion/react'
import { type ReactNode, useEffect, useEffectEvent, useRef } from 'react'
import { cn } from '../../lib/cn.ts'
import { scaleIn } from '../../lib/motion.ts'

// Minimal dialog chrome shared by the reject/snooze/delete/batch-approve surfaces (RAL-108).
// Native <dialog> + showModal() instead of a hand-rolled overlay: focus trapping, Escape-to-close,
// the backdrop, and focus restoration on close all come from the platform for free, and it needs
// no portal — showModal() renders it in the browser's top layer regardless of DOM position.
export interface ModalProps {
  open: boolean
  onClose: () => void
  titleId: string
  children: ReactNode
  className?: string
}

export function Modal({ open, onClose, titleId, children, className }: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
  }, [open])

  // Escape fires a cancelable 'cancel' event before the native, instant 'close'. Prevent
  // that default and route through onClose instead, so Escape gets the same animated exit
  // as every other close path (Cancel button, successful submit) — AnimatePresence's
  // onExitComplete below is what calls the real dialog.close(), once the panel has
  // actually finished animating out.
  const handleCancel = useEffectEvent((event: Event) => {
    event.preventDefault()
    onClose()
  })
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    dialog.addEventListener('cancel', handleCancel)
    return () => dialog.removeEventListener('cancel', handleCancel)
  }, [])

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      className={cn(
        'm-auto max-h-[calc(100vh-3rem)] w-full max-w-4xl border-none bg-transparent p-0 open:flex backdrop:bg-[rgba(5,4,4,.84)]',
        className
      )}
    >
      {/* The dialog element itself just sizes and positions the box (so a caller's
          className, e.g. a narrower max-w, still applies correctly); all visible chrome
          and the enter/exit animation live on this inner panel. */}
      <AnimatePresence onExitComplete={() => dialogRef.current?.close()}>
        {open && (
          <m.div
            key="panel"
            variants={scaleIn}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="flex w-full flex-1 flex-col overflow-y-auto border border-border-hairline-strong bg-ground-canvas text-ink-primary shadow-[8px_8px_0_var(--color-shadow)]"
          >
            {children}
          </m.div>
        )}
      </AnimatePresence>
    </dialog>
  )
}
